import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAcumenCommand } from '../../context/AcumenCommandContext';
import { useAcumen } from '../../context/AcumenContext';
import { useTheme } from '../../context/ThemeContext';
import AcumenBotSettings from './AcumenBotSettings';


const AcumenCommandBar = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ top: 12, right: 80 });
  const [activeSlot, setActiveSlot] = useState(null);
  const [showDropdown, setShowDropdown] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsSlotId, setSettingsSlotId] = useState(null);
  const barRef = useRef(null);
  const dragHandleRef = useRef(null);
  const dropdownRef = useRef(null);

  const { 
    slots, 
    activateTool, 
    deactivateTool, 
    activeTool,
    capturedContext
  } = useAcumenCommand();

  const { darkMode } = useTheme();
  

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        deactivateTool();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [deactivateTool]);

  // Mouse move handler for dragging
  const handleMouseMove = useCallback((event) => {
    if (!isDragging || !barRef.current) return;

    const barRect = barRef.current.getBoundingClientRect();
    const newX = event.clientX - barRect.width / 2;
    const newY = event.clientY - barRect.height / 2;

    setPosition({
      top: Math.max(0, newY),
      right: Math.max(0, window.innerWidth - newX - barRect.width)
    });
  }, [isDragging]);

  // Mouse up handler for dragging
  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    const barRect = barRef.current.getBoundingClientRect();
    const { top, right } = position;

    let dockPosition = null;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Check docking positions
    if (top < 40) {
      dockPosition = 'top';
    } else if (right < 40) {
      dockPosition = 'right';
    } else if (windowWidth - right - barRect.width < 40) {
      dockPosition = 'left';
    } else if (windowHeight - top - barRect.height < 40) {
      dockPosition = 'bottom';
    }

    // Call context function to set position
    if (dockPosition) {
      setPosition({ dock: dockPosition });
    }
  }, [isDragging, position]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleDragStart = (e) => {
    setIsDragging(true);
  };

  const handleSlotDoubleClick = (slot) => {
    deactivateTool(); // always clear cursor mode before opening settings
    setSettingsSlotId(slot.id);
    setShowSettings(true);
  };

  const openSettings = (slotId = null) => {
    deactivateTool(); // clear cursor mode
    setSettingsSlotId(slotId || (slots[0]?.id));
    setShowSettings(true);
  };

  const handleSlotClick = (slot) => {
    if (slot.mode === activeTool) {
      deactivateTool();
      setActiveSlot(null);
      setShowDropdown(null);
      return;
    }

    if (slot.agents.length > 1) {
      setShowDropdown(slot.mode);
      setActiveSlot(slot.mode);
      return;
    }

    activateTool(slot.mode, slot.agents[0]);
    setActiveSlot(slot.mode);
    setShowDropdown(null);
  };

  const handleAgentSelect = (agent, slotMode) => {
    activateTool(slotMode, agent);
    setShowDropdown(null);
    setActiveSlot(slotMode);
  };

  const getSlotStyle = (slot) => {
    const isActive = slot.mode === activeTool;
    const baseColor = slot.color;
    
    if (isActive) {
      return {
        background: baseColor,
        border: `1.5px solid ${baseColor}`,
        color: 'white',
        boxShadow: `0 0 12px ${baseColor}80`,
        transition: 'all 0.15s ease'
      };
    } else {
      return {
        background: `${baseColor}18`,
        border: `1.5px solid ${baseColor}40`,
        color: baseColor,
        transition: 'all 0.15s ease'
      };
    }
  };

  const getSlotLabel = (slot) => {
    if (slot.mode === 'context' && capturedContext.length > 0) {
      return `${slot.label || slot.name} (${capturedContext.length})`;
    }
    return slot.label || slot.name;
  };

  const barStyle = {
    position: 'fixed',
    top: `${position.top}px`,
    right: `${position.right}px`,
    background: darkMode ? '#1a1f2e' : 'white',
    border: `1px solid ${darkMode ? '#2d3748' : '#e5e7eb'}`,
    borderRadius: '12px',
    padding: '6px 10px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    zIndex: 9990,
    cursor: isDragging ? 'grabbing' : 'default'
  };

  const dragHandleStyle = {
    width: '20px',
    height: '20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'move',
    userSelect: 'none',
    padding: '2px'
  };

  const dotStyle = (color) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: color,
    flexShrink: 0
  });

  return (
    <>
    <div ref={barRef} style={barStyle}>
      {/* Drag handle */}
      <div 
        ref={dragHandleRef}
        style={dragHandleStyle}
        onMouseDown={handleDragStart}
      >
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{
            width: '2px',
            height: '2px',
            borderRadius: '50%',
            background: darkMode ? '#a0aec0' : '#718096',
            margin: '1px 0'
          }} />
        ))}
      </div>

      {/* Slot buttons */}
      {slots.map((slot) => (
        <div key={slot.mode}>
          <button
            onClick={() => handleSlotClick(slot)}
            onDoubleClick={() => handleSlotDoubleClick(slot)}
            style={{
              ...getSlotStyle(slot),
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              padding: '0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: '700',
            }}
            title={getSlotLabel(slot)}
          >
            <div style={dotStyle(slot.color)} />
          </button>

          {/* Dropdown for multiple agents */}
          {showDropdown === slot.mode && slot.agents.length > 1 && (
            <div 
              ref={dropdownRef}
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                background: darkMode ? '#2d3748' : 'white',
                border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
                borderRadius: '8px',
                padding: '4px 0',
                zIndex: 10000,
                minWidth: '120px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                marginTop: '4px'
              }}
            >
              {slot.agents.map((agent) => (
                <button
                  key={agent}
                  onClick={() => handleAgentSelect(agent, slot.mode)}
                  style={{
                    width: '100%',
                    padding: '6px 12px',
                    background: 'none',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '11px',
                    color: darkMode ? '#e2e8f0' : '#4a5568'
                  }}
                >
                  <div style={dotStyle(slot.color)} />
                  {agent}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Wash / Clear button */}
      <button
        onClick={deactivateTool}
        data-acumen-ui="true"
        title="Clear — return to normal cursor"
        style={{
          marginLeft: 4,
          background: activeTool ? '#bfdbfe' : (darkMode ? '#1e2435' : '#f0f9ff'),
          border: `1.5px solid #93c5fd`,
          borderRadius: '8px',
          padding: '5px 10px',
          fontSize: '11px',
          fontWeight: 700,
          color: '#3b82f6',
          cursor: 'pointer',
          opacity: activeTool ? 1 : 0.5,
          transition: 'all 0.15s',
        }}
      >
        ↺ CLEAR
      </button>

      {/* Settings button — opens settings without activating any cursor mode */}
      <button
        onClick={() => openSettings()}
        title="Command Bar Settings"
        data-acumen-ui="true"
        style={{
          marginLeft: 2,
          background: 'none',
          border: `1.5px solid ${darkMode ? '#2d3748' : '#e5e7eb'}`,
          borderRadius: '8px',
          padding: '5px 8px',
          fontSize: '13px',
          color: darkMode ? '#64748b' : '#9ca3af',
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
      >
        ⚙️
      </button>

      {/* Active mode indicator — hide when settings is open */}
      {activeTool && !showSettings && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: 6,
          color: slots.find(s => s.mode === activeTool)?.color || '#6b7280',
          fontSize: '11px',
          textAlign: 'center',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          background: darkMode ? '#0f1117' : '#fff',
          padding: '2px 10px',
          borderRadius: 20,
          border: `1px solid ${slots.find(s => s.mode === activeTool)?.color || '#e5e7eb'}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          {slots.find(s => s.mode === activeTool)?.name} MODE — click anything
        </div>
      )}
    </div>

    {/* Bot Settings Modal */}
    {showSettings && (
      <AcumenBotSettings
        activeSlotId={settingsSlotId}
        onClose={() => setShowSettings(false)}
      />
    )}
    </>
  );
};

export default AcumenCommandBar;
